<template>
    <model-form
        :title="
            protocolTaskTemplate && protocolTaskTemplate.id
                ? `Edit Task for ${getTaskDisplayName}`
                : 'Create Data Collection Task'
        "
        :formFields="formFields"
        @save="save"
        @cancel="cancel"
    >
        <template v-slot:dayFieldDescription>
            {{ taskDayDescription }}
        </template>
        <template v-slot:inheritedTaskGroupDescription>
            {{ taskDayDescription }}
        </template>
    </model-form>
</template>
<script>
import { defineComponent } from "vue";
import ModelForm from "../common/models/ModelForm.vue";
import { buildNumberField, buildSelectField, buildSlotField } from "@/lib/vueModelUtils";
import { buildProtocolTaskTemplatesAPI } from "@/services/api.services";
import { transformEmptyStringToNull } from "@/lib/utils";
import {
    buildReadoutGroupOptions,
    findTaskGroupById,
    getTaskDayDescription,
} from "@/lib/taskUtils";
import _ from "lodash";
import { withLoadingScreen } from "@/lib/loading";

export default defineComponent({
    props: {
        protocolTaskTemplate: {
            type: Object,
            required: false,
        },
        readoutGroups: {
            type: Array,
            required: true,
        },
        taskGroups: {
            type: Array,
            required: true,
        },
        protocol: {
            type: Object,
            required: true,
        },
    },
    components: {
        ModelForm,
    },
    data() {
        return {
            protocolTaskTemplatesAPI: null,
            taskDayDescription: "",
            taskGroupField: null,
            readoutGroupField: null,
            typeField: null,
            overrideTaskGroupDayField: null,
            dayField: null,
            inheritedTaskGroupDescriptionField: null,
            formFields: [],
        };
    },
    mounted() {
        this.protocolTaskTemplatesAPI = buildProtocolTaskTemplatesAPI(this.protocol.code);
        this.buildFormFields();
    },
    methods: {
        getDay() {
            if (!this.protocolTaskTemplate.id) {
                return null;
            }

            const taskDay = this.protocolTaskTemplate.day;
            if (!_.isNil(taskDay)) {
                return taskDay;
            } else {
                return ""; // TODO look up selected task group?
            }
        },
        buildFormFields: function () {
            this.taskGroupField = this.buildTaskGroupFormField();
            this.readoutGroupField = this.buildReadoutGroupFormField();
            this.overrideTaskGroupDayField = this.buildOverrideTaskGroupDayFormField();
            this.dayField = this.buildSpecifyDayFormField();

            this.inheritedTaskGroupDescriptionField = buildSlotField({
                slot: "inheritedTaskGroupDescription",
                cols: 12,
            });

            this.updateFormFields();
            this.updateTaskDayDescription();
        },
        buildTaskGroupFormField() {
            const taskGroupOptions = this.taskGroups.map((taskGroup) => {
                return {
                    title: taskGroup.displayName,
                    value: taskGroup.id,
                };
            });

            const taskGroupId = this.protocolTaskTemplate.taskGroupId;

            return buildSelectField({
                name: "Task Group",
                options: taskGroupOptions,
                value: taskGroupId,
                changed: (taskGroupId) => {
                    const taskGroup = findTaskGroupById(taskGroupId, this.taskGroups);
                    this.updateTaskGroupSelection(taskGroup);
                },
            });
        },
        buildReadoutGroupFormField() {
            const readoutGroupOptions = buildReadoutGroupOptions(this.readoutGroups);

            return buildSelectField({
                name: "Readout Group",
                options: readoutGroupOptions,
                value: this.protocolTaskTemplate.readoutGroupId,
            });
        },
        buildOverrideTaskGroupDayFormField() {
            const hasOverriddenValue =
                !_.isNil(this.protocolTaskTemplate.date) || !_.isNil(this.protocolTaskTemplate.day);

            return {
                fieldType: "boolean",
                fieldDisplayName: "Override Task Group Day",
                value: hasOverriddenValue,
                disabled: _.isNil(this.taskGroupField.value),
                changed: () => {
                    this.toggleOverrideTaskGroup();
                },
            };
        },
        buildSpecifyDayFormField() {
            const taskDay = this.protocolTaskTemplate.day;
            const dayField = buildNumberField({ label: "Day", value: taskDay });
            dayField.value = this.getDay();
            dayField.disabled = !this.overrideTaskGroupDayField.value;
            dayField.changed = () => {
                this.updateTaskDayDescription();
            };

            dayField.appendSlot = "dayFieldDescription";

            return dayField;
        },
        updateFormFields() {
            const formFields = [
                this.readoutGroupField,
                this.taskGroupField,
                this.overrideTaskGroupDayField,
            ];

            if (this.overrideTaskGroupDayField.value) {
                formFields.push(this.dayField);
            } else {
                // If the user is not overriding the task group day, show the inherited description
                formFields.push(this.inheritedTaskGroupDescriptionField);
            }

            this.formFields = formFields;
        },
        toggleOverrideTaskGroup() {
            const overrideTaskGroup = this.overrideTaskGroupDayField.value;
            // set the override fields to disabled if not overriding, otherwise enable them.
            this.dayField.disabled = !overrideTaskGroup;
            this.updateFormFields();
            this.updateTaskDayDescription();
        },
        updateTaskGroupSelection(taskGroup) {
            this.overrideTaskGroupDayField.disabled = false;

            if (!this.overrideTaskGroupDayField.value) {
                this.dayField.value = taskGroup.day || 0;
            }

            this.updateFormFields();
            this.updateTaskDayDescription();
        },
        updateTaskDayDescription() {
            this.taskDayDescription = getTaskDayDescription({
                day: this.dayField.value,
                date: null,
                isRelativeDay0: true,
                isInheritedFromTaskGroup: !this.overrideTaskGroupDayField.value,
                selectedTaskGroupId: this.taskGroupField.value,
                taskGroups: this.taskGroups,
            });
        },
        async save() {
            await withLoadingScreen(async () => {
                const protocolTaskTemplate = {
                    ...this.protocolTaskTemplate,
                };

                if (this.overrideTaskGroupDayField.value) {
                    protocolTaskTemplate.day = transformEmptyStringToNull(this.dayField.value);
                } else {
                    protocolTaskTemplate.day = null;
                }
                protocolTaskTemplate.taskGroupId = this.taskGroupField.value;
                protocolTaskTemplate.readoutGroupId = this.readoutGroupField.value;
                if (this.protocolTaskTemplate.id) {
                    await this.protocolTaskTemplatesAPI.update(protocolTaskTemplate);
                } else {
                    await this.protocolTaskTemplatesAPI.create(protocolTaskTemplate);
                }
            });
            this.$emit("saved");
        },
        cancel() {
            this.$emit("closed");
        },
        close() {
            this.$emit("closed");
        },
    },
    computed: {
        getTaskDisplayName() {
            const taskGroup = findTaskGroupById(
                this.protocolTaskTemplate.taskGroupId,
                this.taskGroups
            );
            if (!taskGroup) {
                return "";
            }
            // ProtocolTaskTemplates don't have dates, they only have days.
            return `${taskGroup.displayName} on day ${taskGroup.day}`;
        },
    },
});
</script>
